/** CARDUL PIEȚEI: configurația recomandată de profilul zonei — mixul de
 *  apartamente, standardul care se vinde, prețul net —, același pe brief
 *  (coloana din dreapta, cu aplicarea în temă dedesubt) și sus în «Vânzări în
 *  zonă» (numai de citit). Cifrele vin gata socotite de colector
 *  (`market-price-collector/profil.py`); aici se spun, fiecare cu ce o susține.
 *
 *  MIXUL E O BARĂ ÎMPĂRȚITĂ, în patru trepte ale aceleiași nuanțe: camerele sunt
 *  o ordine (garsonieră → 4+), nu categorii fără legătură între ele, deci o
 *  nuanță, de la deschis la închis (pași de luminozitate vizibili, capătul
 *  deschis peste 2:1 pe alb). Fiecare segment are eticheta lui în legenda de
 *  dedesubt: culoarea nu e singurul fel în care se citește. */
import type { ReactNode } from "react";
import { data, numar, t, tn, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { type ClasaFinisaje, type Colectare, type Profil, inLucru } from "./piata";
import "./businessCase.css";
import "./piata.css";

/** Cât mai ține căutarea, în cuvinte; `completa = false` = o sursă încă își
 *  caută cartierele, deci rămân pagini pe care nu le știm. */
export function catMaiDureaza(c: Pick<Colectare, "estimare">): string {
  const e = c.estimare;
  if (!e || e.secunde <= 0) return t("tema.piata.cat_necunoscut");
  const n = Math.max(1, Math.ceil(e.secunde / 60));
  return t(e.completa ? "tema.piata.cat_min" : "tema.piata.cat_minim", { n });
}

/** Clasa de finisaj a pieței, cu numele treptei din temă în care se aplică. */
const NIVEL: Record<ClasaFinisaje, Cheie> = {
  low: "tema.nivel.buget", standard: "tema.nivel.standard", premium: "tema.nivel.premium", luxury: "tema.nivel.lux",
};

const euro = (v: number | null | undefined) => (v == null ? "—" : `${numar(v, { maximumFractionDigits: 0 })} €`);
const procent = (v: number) => `${numar(v * 100, { maximumFractionDigits: 0 })} %`;
const semn = (d: number) => `${d > 0 ? "+" : "−"}${numar(Math.abs(d) * 100, { maximumFractionDigits: 0 })} %`;

export default function CardPiata({ profil, colectare, jos, gol }: {
  profil: Profil | null;
  /** ultima colectare a proiectului: cât se caută, cifrele sunt provizorii */
  colectare?: Pick<Colectare, "stare" | "estimare"> | null;
  /** rândul de jos: pe brief, aplicarea în temă și detaliile */
  jos?: ReactNode;
  /** ce se spune cât nu e nicio recomandare (pe brief: căutarea, punctul lipsă) */
  gol?: ReactNode;
}) {
  useT();
  const r = profil?.recomandare ?? null;
  const seCauta = inLucru(colectare);
  const oferte = r ? tn("pz.n_oferte#n", r.mix_n) : "";
  return (
    <section className="cp ip-card" aria-labelledby="cp-titlu">
      <div className="cp-cap">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="cp-pin">
          <path d="M10 18s6-5.2 6-10A6 6 0 0 0 4 8c0 4.8 6 10 6 10z" /><circle cx="10" cy="8" r="2.2" />
        </svg>
        <b id="cp-titlu" className="cp-titlu">{t("tema.piata.titlu")}</b>
        {r && (
          <span className="cp-sursa">{seCauta || profil?.colectare?.stare !== "gata"
            ? t("cp.pana_acum", { oferte })
            : t("tema.piata.pe", { oferte, data: data(profil?.colectare?.terminat_la) })}</span>
        )}
        {seCauta && <span className="ip-pill ip-pill-amb" title={t("pz.p.provizoriu_t")}>{t("pz.p.provizoriu")}</span>}
      </div>

      {seCauta && (
        <p className="cp-nota">{colectare!.stare === "ruleaza"
          ? t("cp.se_cauta", { cat: catMaiDureaza(colectare!) }) : t("tema.piata.la_rand")}</p>
      )}

      {r ? (
        <>
          <div className="cp-mix">
            <span className="cp-et">{t("cp.mix")}</span>
            <div className="cp-bara" role="img"
                 aria-label={r.mix.map((m) => `${numar(m.pct)} % ${t(`pz.p.g.${m.grupa}`)}`).join(", ")}>
              {r.mix.map((m) => (
                <span key={m.grupa} className={`cp-seg cp-g${m.grupa}`} style={{ flexGrow: Math.max(m.pct, 1) }}
                      title={t("cp.seg_t", { pct: numar(m.pct), grupa: t(`pz.p.g.${m.grupa}`),
                        suprafata: m.suprafata == null ? "—" : numar(m.suprafata, { maximumFractionDigits: 0 }),
                        pret: euro(m.pret_mp), oferte: tn("pz.n_oferte#n", m.n) })} />
              ))}
            </div>
            <div className="cp-leg">
              {r.mix.map((m) => (
                <span key={m.grupa} className="cp-leg-i">
                  <i className={`cp-g${m.grupa}`} aria-hidden="true" />
                  <b>{numar(m.pct)} %</b> {t(`pz.p.g.${m.grupa}`)}
                  {m.suprafata != null && (
                    <span className="cp-mut">~{numar(m.suprafata, { maximumFractionDigits: 0 })} m²</span>
                  )}
                </span>
              ))}
            </div>
          </div>

          <div className="cp-doua">
            {r.finisaje && (
              <div className="cp-bloc">
                <span className="cp-et">{t("cp.standard")}</span>
                <b className="cp-clasa">{t(NIVEL[r.finisaje.clasa])}</b>
                <span className="cp-mut">
                  {t("cp.standard_pe", { pct: procent(r.finisaje.pct), estimate: tn("pz.n_oferte#n", r.finisaje.n_estimate) })}
                  {r.finisaje.fata_de.length > 0 && <> · {r.finisaje.fata_de
                    .map((d) => `${t(NIVEL[d.clasa])} ${semn(d.dif)}`).join(", ")}</>}
                </span>
              </div>
            )}
            <div className="cp-bloc">
              <span className="cp-et">{t("cp.pret")}</span>
              <b className="cp-pret">{t("cp.pret_net", { pret: euro(r.pret.net_mp) })}</b>
              <span className="cp-mut">{t("cp.pret_pe", { cerut: euro(r.pret.cerut_mp) })}</span>
            </div>
          </div>
        </>
      ) : (
        gol ?? (profil && (
          <p className="cp-nota">{profil.motiv === "putine"
            ? t("pz.p.rec.putine", { min: tn("pz.n_oferte#n", profil.setari.min_oferte), n: numar(profil.esantion.n) })
            : t("pz.p.rec.fara_oferte")}</p>
        ))
      )}

      {jos && <div className="cp-jos">{jos}</div>}
    </section>
  );
}
